/**
 * The six verbs, the composer, and the marks already on a clause.
 *
 * Two principles do most of the work here.
 *
 * **Private by default, published deliberately.** A mark is yours until you
 * say otherwise, and the composer says so in words rather than leaving it to
 * an unchecked box. Nobody writes down what they actually think of their
 * employer in a tool where they are unsure who is reading.
 *
 * **Anonymity is a property of the mark, chosen while writing it.** The design
 * brief puts the toggle in the composer and not in settings, and it is right:
 * a preference set once, days earlier, is not a decision anyone can remember
 * making about the sentence in front of them.
 */

import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PlacedAnnotation, type Verb } from '../lib/api.js';
import type { Selection } from './Annotatable.js';
import { isSettled, markCount, openFirst } from './settled.js';

/**
 * A selection that has had its W3C context selectors attached.
 *
 * The bar works on a bare screen selection; the composer needs the prefix and
 * suffix, because those are what let the mark find its words again after the
 * text around them shifts.
 */
export type AnchoredSelection = Selection & { prefix: string; suffix: string };

/**
 * Glyph and label per verb, in the order the design brief fixes them.
 *
 * Typographic glyphs, not emoji. The design language is explicit — "outline
 * icons only… typographic glyphs rather than icon files. Never use filled or
 * pictorial icons" — and 🖍 📝 ❓ 💬 ⚖ ▣ broke it six times in the one place a
 * person looks most. Emoji also render as full-colour pictures on every
 * platform, which is the single loudest thing that can appear inside a canvas
 * where saturated colour is reserved for the seven collaborators.
 *
 * These come from the glyph table in the README's §7 and are drawn from the
 * text families already loaded, so they inherit weight and colour like the
 * words beside them and disappear correctly in a monochrome print.
 */
export const VERBS: { verb: Verb; glyph: string; label: string; hint: string }[] = [
  { verb: 'highlight', glyph: '❯', label: 'Highlight', hint: 'Mark it without saying why' },
  { verb: 'note', glyph: '✎', label: 'Note', hint: 'What you think about it' },
  { verb: 'question', glyph: '?', label: 'Question', hint: 'Something to put to the employer' },
  { verb: 'ask', glyph: '◇', label: 'Ask', hint: 'Ask the workbench about it' },
  { verb: 'compare', glyph: '⇌', label: 'Compare', hint: 'Against the Award or the public system' },
  { verb: 'claim', glyph: '□', label: 'Claim', hint: 'Start a claim with these exact words' },
];

/** The bar that appears at a selection. One gesture from problem to claim. */
export function ActionBar({
  at,
  onVerb,
}: {
  at: { top: number; left: number };
  onVerb: (verb: Verb) => void;
}) {
  return (
    /*
      Ink, not paper.
      
      A pale bar with a hairline sat on a pale page and read as part of the
      document — which is wrong twice over: it is the most consequential
      control in the product, and it is floating over text a person has just
      chosen, so it has to be unmistakably not-the-document. The dark ground is
      the one inversion the design language allows, and it is what the mockup
      does.
    */
    <div
      role="toolbar"
      aria-label="What to do with this selection"
      className="absolute z-20 flex items-center gap-0.5 rounded-[8px] p-1"
      style={{
        // Already flipped by readSelection, which knows whether there is room
        // above the selection. Subtracting here as well put the bar off the
        // top of the block for anything selected on a first line.
        top: at.top,
        left: at.left,
        background: 'var(--color-ink)',
        color: 'var(--color-paper)',
      }}
    >
      {VERBS.map(({ verb, glyph, label, hint }) => (
        <button
          key={verb}
          onMouseDown={(event) => {
            // mousedown, not click: a click would clear the selection the bar
            // exists to act on before the handler ever ran.
            event.preventDefault();
            onVerb(verb);
          }}
          title={hint}
          /*
            Claim carries the emphasis, because it is the gesture the whole
            product is built around: one selection becomes a draft claim with
            the verbatim words attached. The others are ways of thinking about
            a passage; this is the one that puts it to the employer.
          */
          className={`hit flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-[12px] ${
            verb === 'claim' ? 'font-medium' : ''
          }`}
          style={
            verb === 'claim'
              ? { background: 'var(--color-paper)', color: 'var(--color-ink)' }
              : undefined
          }
        >
          <span aria-hidden className="opacity-70">
            {glyph}
          </span>
          {label}
        </button>
      ))}
    </div>
  );
}

/** The composer, opened by a verb that needs words. */
export function Composer({
  clause,
  verb,
  selection,
  onAsk,
  onCompare,
  onDone,
  onCancel,
}: {
  clause: string;
  verb: Verb;
  selection: AnchoredSelection;
  /** Where "Ask the workbench about it" actually goes. */
  onAsk: (seed: string) => void;
  /** Where "Against the Award or the public system" actually goes. */
  onCompare: (number: string) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [body, setBody] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [publish, setPublish] = useState(false);
  const [claimType, setClaimType] = useState('improve');
  const [error, setError] = useState<string | null>(null);

  const entry = VERBS.find((v) => v.verb === verb)!;

  const create = useMutation({
    mutationFn: async () => {
      // The Claim verb is the single most important gesture in the product:
      // one selection becomes a draft claim with the verbatim text already
      // captured. The mark is made too — anonymously if asked — so the trail
      // from the claim back to the words that prompted it survives, but the
      // claim itself is group work and always carries the drafter as owner.
      if (verb === 'claim') {
        const claim = await api.createClaim({
          title: body.trim(),
          type: claimType,
          clause,
          verbatim: selection.exact,
        });
        await api.annotate({
          clause,
          verb,
          body: `${claim.ref}: ${body.trim()}`,
          exact: selection.exact,
          prefix: selection.prefix,
          suffix: selection.suffix,
          start: selection.start,
          end: selection.end,
          isAnonymous: false,
          visibility: 'team',
        });
        return;
      }

      /*
       * A question is raised, not just noted.
       *
       * The Claim verb has always bridged: it creates the claim and leaves a
       * mark, so one gesture puts the words in the workbench and a trace on
       * the text. Question did not. It wrote an annotation and stopped, while
       * the Questions ledger reads from a table only the ledger's own form
       * ever wrote to — so a question raised from the document was stored,
       * invisible, and looked to the person who wrote it like it had been
       * dropped.
       *
       * The ledger row goes first. If it fails the mark is not made either,
       * which is the right way round: a mark with no ledger row is the silent
       * state we are removing, and a ledger row with no mark is merely a
       * question whose provenance is thinner.
       */
      if (verb === 'question') {
        /*
         * Refused rather than skipped.
         *
         * The ledger requires five characters. Making the bridge conditional
         * on that would reintroduce the bug in miniature — a two-word question
         * would save a mark, write no ledger row, and report success, which is
         * precisely the silent drop being fixed.
         */
        if (body.trim().length < 5) {
          throw new Error('Write the question in a sentence, so the group can answer it.');
        }
        await api.raiseQuestion({
          text: body.trim(),
          isAnonymous: anonymous,
          clauses: [clause],
        });
      }

      await api.annotate({
        clause,
        verb,
        body: body.trim() || undefined,
        exact: selection.exact,
        // Context comes from the clause text on the server side of the
        // anchor; here the selection is enough to reconstruct it.
        prefix: selection.prefix,
        suffix: selection.suffix,
        start: selection.start,
        end: selection.end,
        isAnonymous: anonymous,
        visibility: publish ? 'team' : 'private',
      });
    },
    /*
     * Two verbs promised an action and performed none.
     *
     * "Ask the workbench about it" and "Against the Award or the public
     * system" both wrote an annotation and stopped, so the passage a person
     * selected went nowhere and the label was a lie — the same shape as the
     * Question verb that never reached the ledger, minus the missing row.
     *
     * The mark is still made. It is the record that this passage is what
     * prompted the question, and it is what the gutter shows. The navigation
     * is what the label promised on top of it.
     */
    onSuccess: () => {
      onDone();
      if (verb === 'ask') onAsk(body.trim() || selection.exact);
      if (verb === 'compare') onCompare(clause);
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div
      className="mt-4 border-l-2 pl-4"
      style={{ borderColor: 'var(--color-ink)' }}
      role="dialog"
      aria-label={`${entry.label} on this selection`}
    >
      <p className="label">
        {entry.glyph} {entry.label}
      </p>

      <blockquote
        className="register-reading mt-2 border-l pl-3 text-[13px]"
        style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}
      >
        {selection.exact}
      </blockquote>

      <textarea
        autoFocus
        rows={3}
        aria-label="What you want to say about this passage"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={entry.hint}
        className="mt-3 w-full border-b bg-transparent py-1 text-[14px] outline-none"
        style={{ borderColor: 'var(--color-rule)' }}
      />

      {verb === 'claim' ? (
        <div className="mt-3">
          <label className="label mr-2" htmlFor="claim-type">
            This claim would
          </label>
          <select
            id="claim-type"
            value={claimType}
            onChange={(e) => setClaimType(e.target.value)}
            className="border-b bg-transparent py-0.5 text-[12px] outline-none"
            style={{ borderColor: 'var(--color-rule)' }}
          >
            <option value="improve">Improve what is there</option>
            <option value="protect">Protect what is there</option>
            <option value="correct">Correct an error</option>
            <option value="new">Add something new</option>
          </select>
          <p className="mt-2 text-[11px]" style={{ color: 'var(--color-muted)' }}>
            A draft claim is created with these exact words attached, visible to the
            representatives. Claims are group work and carry your name.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
              />
              Don’t attach my name
            </label>

            <label className="flex items-center gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
              />
              Share with the other representatives now
            </label>
          </div>

          <p className="mt-2 text-[11px]" style={{ color: 'var(--color-muted)' }}>
            {publish
              ? anonymous
                ? 'The group will see this straight away, without your name on it.'
                : 'The group will see this straight away, with your name on it.'
              : 'Only you will see this until you publish it.'}
          </p>
        </>
      )}

      {error ? (
        <p className="mt-2 text-[12px]" style={{ color: 'var(--color-ink)' }}>
          {error}
        </p>
      ) : null}

      <div className="mt-3 flex gap-3">
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending}
          className="border px-3 py-1 text-[12px]"
          style={{ borderColor: 'var(--color-ink)' }}
        >
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} className="hit label underline">
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The marks on this clause, listed below the text.
 *
 * The gutter shows who; this shows what they said. Your own private marks
 * appear here too, labelled, because private-by-default only works if you can
 * see what you have not yet published.
 */
export function MarkList({
  clause,
  annotations,
  activeId,
  onActivate,
}: {
  clause: string;
  annotations: readonly PlacedAnnotation[];
  activeId: string | null;
  onActivate: (id: string | null) => void;
}) {
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['annotations', clause] });
    void client.invalidateQueries({ queryKey: ['unpublished'] });
  };

  if (annotations.length === 0) return null;

  // Live marks first. A settled one is still evidence the clause was
  // contested, so it stays in the list rather than being filtered out — but it
  // is not work outstanding, and it should not be the first thing read.
  const ordered = openFirst(annotations);

  return (
    <section className="mt-8 border-t pt-4" style={{ borderColor: 'var(--color-rule)' }}>
      {/* The count, because "Marks on this clause" reads the same over one
          note and over eleven, and how much the group has said about a clause
          is itself the signal a rep is scanning for. The settled count is
          stated separately: eleven answered questions and eleven live ones are
          opposite findings and used to render as the same sentence. */}
      <p className="label">{markCount(annotations).label}</p>
      <ul className="mt-3">
        {ordered.map((mark) => (
          <Mark
            key={mark.id}
            mark={mark}
            active={mark.id === activeId}
            onActivate={onActivate}
            onChanged={refresh}
          />
        ))}
      </ul>
    </section>
  );
}

function Mark({
  mark,
  active,
  onActivate,
  onChanged,
}: {
  mark: PlacedAnnotation;
  active: boolean;
  onActivate: (id: string | null) => void;
  onChanged: () => void;
}) {
  const [showReplies, setShowReplies] = useState(false);
  const [settling, setSettling] = useState(false);
  const [note, setNote] = useState('');
  const hue = mark.authorColour ? `var(--color-${mark.authorColour})` : 'var(--color-muted)';
  const entry = VERBS.find((v) => v.verb === mark.verb);
  const settled = isSettled(mark);

  const publish = useMutation({
    mutationFn: (visibility: 'private' | 'team' | 'cohort') =>
      api.updateAnnotation(mark.id, { visibility }),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteAnnotation(mark.id),
    onSuccess: onChanged,
  });
  const resolve = useMutation({
    mutationFn: () => api.resolveAnnotation(mark.id, note.trim()),
    onSuccess: () => {
      setSettling(false);
      setNote('');
      onChanged();
    },
  });
  const reopen = useMutation({
    mutationFn: () => api.reopenAnnotation(mark.id),
    onSuccess: onChanged,
  });

  return (
    /*
      A note, not a row in a list.

      This was a list item separated by a hairline, on the same ground as the
      clause above it — so the only content in this product a person actually
      wrote read as more document, and was the quietest thing on the page. The
      warm ground and the author's hue as a left edge make a clause that has
      been marked look different from one that has not, before a word is read.

      The hue moves from a 8px dot to the edge, which is the same rule stated
      more loudly: colour is people, never meaning. The dot stays as well,
      because the edge alone is colour carrying identity, and the name beside
      it is what actually says who.
    */
    <li
      className={`note mb-3 ${active ? 'note-active' : ''} ${
        mark.visibility === 'private' ? 'note-private' : ''
      } ${settled ? 'note-settled' : ''}`}
      style={{ '--note-hue': hue } as CSSProperties}
      onMouseEnter={() => onActivate(mark.id)}
      onMouseLeave={() => onActivate(null)}
    >
      <div className="flex items-baseline gap-2">
        <span
          aria-hidden
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ background: hue }}
        />
        <span className="label">
          {mark.isAnonymous ? 'Anonymous' : (mark.authorName ?? 'Someone')}
        </span>
        <span className="label">
          {entry?.glyph} {entry?.label ?? mark.verb}
        </span>

        {mark.visibility === 'private' ? (
          <span className="label" title="Only you can see this">
            ◌ private
          </span>
        ) : null}

        {settled ? (
          <span className="label" style={{ color: 'var(--color-win)' }}>
            ✓ settled
          </span>
        ) : null}
      </div>

      {/*
        Promoted out of the label row.

        "⚠ detached" sat among five other micro-labels in 9px mono, which is
        the least noticeable place on the card — and it means the words this
        mark was written about are no longer in the agreement. On a bargaining
        document that is not a status, it is the whole point: someone objected
        to a sentence and the sentence has moved or gone.
      */}
      {mark.orphaned ? (
        <div className="callout mt-2" style={{ '--callout-hue': 'var(--color-defect)' } as CSSProperties}>
          {/* △, not ⚠. U+26A0 is Extended_Pictographic and renders as a
              full-colour emoji, which is the loudest thing that can appear in
              a canvas where saturated colour belongs to the seven
              collaborators. △ is the handoff's glyph for a defect. */}
          <span className="callout-glyph" aria-hidden>
            △
          </span>
          <p className="text-[12px] leading-snug">
            The words this was written about have changed or gone. The quote below is what they
            said at the time.
          </p>
        </div>
      ) : null}

      <blockquote
        className="register-reading mt-2 border-l pl-3 text-[12px]"
        style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}
      >
        {mark.exact}
      </blockquote>

      {mark.body ? (
        <p className="register-reading mt-2 text-[14px]">{mark.body}</p>
      ) : null}

      {/*
        The resolution, given the weight the question had.

        This is the part worth keeping. "We checked Schedule C and it does
        cover this" is worth more six months from now, mid-negotiation, than
        the original question was — and the alternative on offer before this
        existed was deleting the mark, which threw exactly that away.
      */}
      {settled ? (
        <div className="callout mt-2" style={{ '--callout-hue': 'var(--color-win)' } as CSSProperties}>
          <span className="callout-glyph" aria-hidden>
            ✓
          </span>
          <div>
            <p className="label">Settled by {mark.resolvedByName ?? 'someone'}</p>
            {mark.resolutionNote ? (
              <p className="register-reading mt-1 text-[13px]">{mark.resolutionNote}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {settling ? (
        <form
          className="mt-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (note.trim().length >= 5) resolve.mutate();
          }}
        >
          <label className="label" htmlFor={`settle-${mark.id}`}>
            What settled it
          </label>
          <textarea
            id={`settle-${mark.id}`}
            autoFocus
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="We checked Schedule C and it does cover this."
            className="mt-1 w-full border-b bg-transparent py-1 text-[13px] outline-none"
            style={{ borderColor: 'var(--color-rule)' }}
          />
          <p className="mt-1 text-[11px]" style={{ color: 'var(--color-muted)' }}>
            The mark stays on the text, quietened. Nothing is deleted.
          </p>
          <div className="mt-2 flex gap-3">
            <button
              type="submit"
              disabled={note.trim().length < 5 || resolve.isPending}
              className="border px-3 py-1 text-[12px]"
              style={{
                borderColor:
                  note.trim().length < 5 ? 'var(--color-rule)' : 'var(--color-ink)',
                color: note.trim().length < 5 ? 'var(--color-muted)' : 'var(--color-ink)',
              }}
            >
              {resolve.isPending ? 'Settling…' : 'Settle it'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSettling(false);
                setNote('');
              }}
              className="hit label underline"
            >
              Cancel
            </button>
          </div>
          {resolve.isError ? (
            <p className="mt-2 text-[12px]">{(resolve.error as Error).message}</p>
          ) : null}
        </form>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-3">
        {mark.isMine && mark.visibility === 'private' ? (
          <button onClick={() => publish.mutate('team')} className="hit label underline">
            Share with the group
          </button>
        ) : null}
        {mark.isMine && mark.visibility !== 'private' ? (
          <button onClick={() => publish.mutate('private')} className="hit label underline">
            Make private again
          </button>
        ) : null}
        <button
          onClick={() => setShowReplies((open) => !open)}
          aria-expanded={showReplies}
          className="hit label underline"
        >
          {mark.replies > 0 ? `${mark.replies} replies` : 'Reply'}
        </button>

        {/*
          Anyone's mark, not only your own — the one control on this card that
          is not scoped to its author. A question one person raised is usually
          answered by another, and requiring the asker to come back and close
          it is how a queue stops being maintained.
        */}
        {settled ? (
          <button
            onClick={() => reopen.mutate()}
            className="hit label underline"
            title="Put it back on the list. The resolution note goes with it."
          >
            Reopen
          </button>
        ) : (
          <button
            onClick={() => setSettling((open) => !open)}
            aria-expanded={settling}
            className="hit label underline"
          >
            Settle
          </button>
        )}

        {mark.isMine ? (
          <button onClick={() => remove.mutate()} className="hit label underline">
            Delete
          </button>
        ) : null}
      </div>

      {showReplies ? <Replies annotationId={mark.id} /> : null}
    </li>
  );
}

function Replies({ annotationId }: { annotationId: string }) {
  const client = useQueryClient();
  const [body, setBody] = useState('');
  const [anonymous, setAnonymous] = useState(false);

  const replies = useQuery({
    queryKey: ['replies', annotationId],
    queryFn: () => api.replies(annotationId),
  });

  const add = useMutation({
    mutationFn: () => api.reply(annotationId, { body, isAnonymous: anonymous }),
    onSuccess: () => {
      setBody('');
      void client.invalidateQueries({ queryKey: ['replies', annotationId] });
    },
  });

  return (
    <div className="mt-3 border-l pl-3" style={{ borderColor: 'var(--color-rule)' }}>
      {(replies.data?.replies ?? []).map((r) => (
        <p key={r.id} className="register-reading mb-2 text-[13px]">
          <span className="label mr-2">{r.isAnonymous ? 'Anonymous' : r.authorName}</span>
          {r.body}
        </p>
      ))}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (body.trim()) add.mutate();
        }}
      >
        <input
          value={body}
          aria-label="Your reply"
          onChange={(e) => setBody(e.target.value)}
          placeholder="Reply"
          className="w-full border-b bg-transparent py-1 text-[13px] outline-none"
          style={{ borderColor: 'var(--color-rule)' }}
        />
        <label className="mt-2 flex items-center gap-2 text-[11px]">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          Don’t attach my name
        </label>
      </form>
    </div>
  );
}
